"use client";

import Link from "next/link";
import type { CShopOrderCompleteContent } from "@/lib/api/cms-catalog";
import { useT } from "@/lib/i18n/context";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import { cShopTranslations } from "./CShop.i18n";
import styles from "./CShop.module.css";
import { OrderSteps } from "./CShop";

/* ─── Krok 3 - potwierdzenie zamowienia (`c_shop` view=order-complete) ── */

/* payment_type z m_shop_payment_options (backend `define.php`:
   M_SHOP_PAYMENT_OPTIONS_PAYMENT_TYPE_*). Trzymamy tu tylko 3 typy widoczne
   w potwierdzeniu zamowienia - reszta (karta terminal / pobranie / kompensata)
   przychodzi z payment_info=null z backendu. */
const PAYMENT_TYPE_TRANSFER = 1;
const PAYMENT_TYPE_ONLINE = 2;
const PAYMENT_TYPE_CASH = 3;

/**
 * CShopOrderComplete - widok kroku 3 flow zamawiania (potwierdzenie po sukcesie
 * IAPI_createOrder).
 *
 * Renderuje:
 *   - wskaznik krokow (aktywny krok 3)
 *   - naglowek podziekowania z numerem zamowienia
 *   - kwota do zaplaty + info platnosci (paidBanner LUB blok payment_info)
 *   - buttony: "Twoje zamowienia" (dla logged) + "Zaplac online" (gdy skonfigurowana)
 *
 * `content.order === null` gdy backend nie znalazl zamowienia albo nie nalezy
 * do zalogowanego usera - renderujemy fallback "not found" zamiast crashowac.
 *
 * `payment_info` to strukturalne dane (`{type, info}` lub `null`) - label
 * bierzemy z i18n na podstawie `type`, a `info` (surowa tresc z admina, np.
 * numer konta bankowego) renderujemy z zachowaniem newline'ow przez CSS
 * `white-space: pre-line` - bez `dangerouslySetInnerHTML`.
 */
export function CShopOrderComplete({
  content,
}: {
  content: CShopOrderCompleteContent;
}) {
  const t = useT(cShopTranslations);

  const yourOrdersHref =
    content.href.your_orders !== null
      ? extractInternalHref(content.href.your_orders)
      : null;

  const showYourOrdersButton =
    content.is_logged && content.href.your_orders !== null;
  const showPaymentButton =
    !content.is_paid &&
    content.payment_online_available &&
    content.href.payment_for_order !== null;

  const paymentInfoLabel = ((): string | null => {
    if (content.payment_info === null) return null;
    const hasInfo = content.payment_info.info !== "";
    switch (content.payment_info.type) {
      case PAYMENT_TYPE_TRANSFER:
        return t.orderComplete.paymentInfoTransfer;
      case PAYMENT_TYPE_ONLINE:
        return hasInfo
          ? t.orderComplete.paymentInfoOnlineWithAccount
          : t.orderComplete.paymentInfoOnline;
      case PAYMENT_TYPE_CASH:
        return t.orderComplete.paymentInfoCash;
      default:
        return null;
    }
  })();

  return (
    <div className={styles.root}>
      <OrderSteps steps={content.steps} />

      {content.order === null ? (
        <div className={styles.orderCompleteNotFound}>
          {t.orderComplete.notFound}
        </div>
      ) : (
        <>
          <h2 className={styles.orderCompleteHeading}>
            {t.orderComplete.heading.replace("{nr}", content.order.nr)}
          </h2>

          <div className={styles.orderCompleteBody}>
            <p className={styles.orderCompleteToPay}>
              {t.orderComplete.toPaySum.replace(
                "{amount}",
                t.formatPrice(
                  content.order.order_total_price_brutto,
                  content.order.order_currency,
                ),
              )}
            </p>

            <div className={styles.orderCompleteNote}>
              {t.orderComplete.orderNr.replace("{nr}", content.order.nr)}
            </div>

            {content.is_paid ? (
              <div
                className={`${styles.orderCompleteNote} ${styles.orderCompleteNotePaid}`}
              >
                {t.orderComplete.paidBanner}
              </div>
            ) : (
              paymentInfoLabel !== null && (
                <div className={styles.orderCompleteNote}>
                  <strong>{paymentInfoLabel}</strong>
                  {content.payment_info !== null &&
                    content.payment_info.info !== "" && (
                      <div className={styles.orderCompletePaymentInfoDetails}>
                        {content.payment_info.info}
                      </div>
                    )}
                </div>
              )
            )}
          </div>

          <div className={styles.actions}>
            <div className={styles.actionsLeft}>
              {showYourOrdersButton &&
                (yourOrdersHref !== null ? (
                  <Link
                    href={yourOrdersHref}
                    className={styles.buttonSecondary}
                  >
                    {t.orderComplete.yourOrders}
                  </Link>
                ) : (
                  <a
                    href={content.href.your_orders!}
                    className={styles.buttonSecondary}
                  >
                    {t.orderComplete.yourOrders}
                  </a>
                ))}
            </div>
            <div className={styles.actionsRight}>
              {showPaymentButton && (
                /* Payment URL to zewnetrzny endpoint backendu (`/IPAYMENT/...` na hoscie
                   Imperial), NIE trasa React CMS - dlatego zawsze `<a>`, nie `<Link>`.
                   extractInternalHref stripowaloby host i Next.js probowal navigate
                   wewnatrz CMS-a (`cms.host/IPAYMENT/...` -> 404). */
                <a
                  href={content.href.payment_for_order!}
                  className={styles.buttonPrimary}
                >
                  {t.orderComplete.goToPayment}
                </a>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
